import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  getUserInfoWithLambda,
  type FirestoreUserProfile,
} from "~/services/server/user-tenant/getUserInfoWithLambda";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { S3ServiceLambda } from "~/services/s3";
import {
  RECEIPT_SIGNATURE_FILENAME,
  findReceiptSignatureUrl,
} from "~/utils/finance/receiptSignature";
import { INTENTS, ROUTES } from "~/utils";
import type { SaveReceiptSignatureResult } from "~/types/receiptSignature";
import * as APITypes from "~/API";

export type AccountPageLoaderType = {
  profile: FirestoreUserProfile | null;
  user: APITypes.User;
  receiptSignatureUrl: string | null;
};

export type { SaveReceiptSignatureResult };

export async function AccountPageLoader({
  request,
}: LoaderFunctionArgs): Promise<AccountPageLoaderType | Response> {
  const firebaseUser = await getReadyAuthUser();
  if (!firebaseUser) return redirect("/explore");

  const userInfo = await getUserInfoWithLambda({ userID: firebaseUser.uid });
  if (!userInfo.success) {
    throw new Error(
      typeof userInfo.error === "string"
        ? userInfo.error
        : "Failed to load account",
    );
  }

  let receiptSignatureUrl: string | null = null;
  const filesResult = await S3ServiceLambda.list({
    entityType: APITypes.EntityType.User,
    entityID: firebaseUser.uid,
  });
  if (filesResult.success) {
    receiptSignatureUrl = findReceiptSignatureUrl(filesResult.data.all);
  }

  return {
    profile: userInfo.profile,
    user: userInfo.data,
    receiptSignatureUrl,
  };
}

export async function AccountPageAction({
  request,
}: ActionFunctionArgs): Promise<SaveReceiptSignatureResult | { error: string }> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    return redirect(ROUTES.home) as never;
  }

  const formData = await request.formData();
  const intent = formData.get("intent");

  switch (intent) {
    case INTENTS._save_receipt_signature: {
      const signature = formData.get("signature");
      if (!(signature instanceof File) || signature.size === 0) {
        return { success: false, error: "Signature file is required" };
      }

      const uploadResult = await S3ServiceLambda.upload({
        entityType: APITypes.EntityType.User,
        entityID: user.uid,
        file: signature,
        name: RECEIPT_SIGNATURE_FILENAME,
      });

      if (!uploadResult.success) {
        return {
          success: false,
          error:
            typeof uploadResult.error === "string"
              ? uploadResult.error
              : "Failed to save receipt signature",
        };
      }

      const filesResult = await S3ServiceLambda.list({
        entityType: APITypes.EntityType.User,
        entityID: user.uid,
      });

      const receiptSignatureUrl = filesResult.success
        ? findReceiptSignatureUrl(filesResult.data.all)
        : null;

      return { success: true, receiptSignatureUrl };
    }
    default:
      return { error: "Invalid intent" };
  }
}
